import { useCatalogTaxonomyText, useCatalogTaxonomyDialog } from "../catalogTaxonomyMessages";
import { useEffect, useMemo, useState } from "react";
import axios from "../../../api/axiosInstance";
import { readStoredUser } from "../../../utils/sessionUser";
import useCatalogAccess from "../useCatalogAccess";
import {

  buildSearchBlob,
  getItemHierarchy,
  truncateItemsFamiliesText,
} from "../itemsFamiliesUi";

const LEVEL_LABELS = {
  family: "Family",
  subfamily: "Subfamily",
  category: "Category",
  subcategory: "Subcategory",
};

export function makeTaxonomyItemsScope(level, id, name) {
  const normalizedId = Number(id);
  if (!LEVEL_LABELS[level] || !Number.isInteger(normalizedId) || normalizedId <= 0) return null;

  return {
    level,
    id: normalizedId,
    name: String(name || "-").trim() || "-",
  };
}

export function TaxonomyItemsLink({ scope, onOpen, children, strong = false, className = "" }) {
  const t = useCatalogTaxonomyText();
  const user = readStoredUser();
  if (!scope || !(Number(user?.id) === 1 || user?.permissions?.["purchasing.items.view"] === true)) return <span className={className}>{children || "-"}</span>;

  return (
    <button
      type="button"
      className={`ifm-taxonomyLink ${strong ? "ifm-taxonomyLink--strong" : ""} ${className}`.trim()}
      onClick={() => onOpen(scope)}
      title={t(`View items in ${LEVEL_LABELS[scope.level]}: ${scope.name}`)}
    >
      {children || scope.name}
    </button>
  );
}

export default function TaxonomyItemsPopup({ scope, onClose }) {
  const access = useCatalogAccess(readStoredUser()?.active_business_unit_id);
  const permission = "purchasing.items.view";
  const canView = access.can(permission);
  const t = useCatalogTaxonomyText();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const dialogRef = useCatalogTaxonomyDialog(Boolean(scope), onClose, false);

  useEffect(() => {
    if (!scope || !canView) return undefined;
    const stamp = access.capture();

    let cancelled = false;
    const parameterName = `${scope.level}_ids`;

    const loadItems = async () => {
      setLoading(true);
      setError("");
      setSearch("");
      setItems([]);

      try {
        const response = await axios.get("/api/purchasing/items/by-hierarchy", {
          ...access.requestConfig(permission),
          params: { ...access.requestConfig(permission).params, [parameterName]: scope.id },
        });
        if (!cancelled && access.isCurrent(stamp, permission)) setItems(Array.isArray(response.data) ? response.data : []);
      } catch (requestError) {
        console.error("[TAXONOMY][ITEMS][LOAD][ERROR]", requestError);
        if (!cancelled && access.isCurrent(stamp, permission)) {
          setError(requestError?.response?.data?.error || "We could not load the items in this catalog branch.");
        }
      } finally {
        if (!cancelled && access.isCurrent(stamp, permission)) setLoading(false);
      }
    };

    loadItems();
    return () => {
      cancelled = true;
    };
  }, [scope, access, canView]);

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return items;

    return items.filter((item) => {
      const hierarchy = getItemHierarchy(item);
      return buildSearchBlob([hierarchy.family, hierarchy.subfamily, hierarchy.category, hierarchy.subcategory, item?.code, item?.name])
        .includes(query);
    });
  }, [items, search]);

  if (!scope || !canView) return null;

  return (
    <div
      className="spx-modalBackdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >


      <div ref={dialogRef} className="spx-modal spx-modal--wide" role="dialog" aria-modal="true" aria-label={t("Items in taxonomy branch")}>
        <div className="spx-modalHeader">
          <div>
            <div className="spx-modalKicker">{t("Catalog Branch")}</div>
            <h3 className="spx-modalTitle">{scope.name}</h3>
            <p className="spx-modalSubtitle">{t(`Items assigned to this ${LEVEL_LABELS[scope.level].toLowerCase()}.`)}</p>
          </div>
          <button type="button" className="spx-closeBtn" onClick={onClose} aria-label={t("Close items popup")}>
            x
          </button>
        </div>

        <div className="spx-modalBody">
          <div className="ifm-modalToolbar">
            <input
              type="search"
              className="spx-search"
              aria-label={t("Search")}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("Search within these items...")}
              disabled={loading}
            />
            <div className="spx-note">{t("Showing")} <strong>{filteredItems.length}</strong> {t("of")} <strong>{items.length}</strong> {t("items.")} </div>
          </div>

          {loading ? <div className="spx-empty">{t("Loading items...")}</div> : null}
          {!loading && error ? <div className="spx-banner spx-banner--error">{t(error)}</div> : null}
          {!loading && !error && !filteredItems.length ? (
            <div className="spx-empty">{t(items.length ? "No items match this search." : "There are no items in this catalog branch.")}</div>
          ) : null}
          {!loading && !error && filteredItems.length ? (
            <div className="spx-tableWrap">
              <table className="spx-table ifm-taxonomyItemsTable">
                <thead>
                  <tr>
                    <th>{t("Family")}</th>
                    <th>{t("Subfamily")}</th>
                    <th>{t("Category")}</th>
                    <th>{t("Subcategory")}</th>
                    <th>{t("Code")}</th>
                    <th>{t("Item")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((item) => {
                    const hierarchy = getItemHierarchy(item);
                    return (
                      <tr key={item.id}>
                        <td className="spx-muted">{hierarchy.family}</td>
                        <td className="spx-muted">{hierarchy.subfamily}</td>
                        <td className="spx-muted">{hierarchy.category}</td>
                        <td className="spx-muted">{hierarchy.subcategory}</td>
                        <td className="spx-code">{item?.code || "-"}</td>
                        <td title={item?.name || "-"}>
                          <span className="spx-strong">{truncateItemsFamiliesText(item?.name || "-", 72)}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
